"use client";

import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { settingsApi } from "@/lib/api";
import { useConnection } from "./ConnectionContext";

export interface ThemeSettings {
  logoUrl: string;
  primaryColor: string;
  secondaryColor: string;
  fontFamily: string;
}

export interface BusinessInfo {
  businessName: string;
  tagline: string;
  address: string;
  phone: string;
  email: string;
  website: string;
  gstNumber: string;
  licenseNumber: string;
}

export interface PWASettings {
  appName: string;
  shortName: string;
  description: string;
  icon192: string;
  icon512: string;
  installPromptEnabled: boolean;
}

export interface GeneralSettings {
  language: string;
  timezone: string;
  dateFormat: string;
  currency: string;
  taxRate: string;
  quotationValidityDays: string;
  testingMode: boolean;
  quickLoginEmail: string;
  quickLoginPassword: string;
  /** Admin password required to edit a recorded client payment. Write-only from the UI. */
  paymentEditPassword: string;
}

interface AppSettings {
  theme: ThemeSettings;
  business: BusinessInfo;
  pwa: PWASettings;
  general: GeneralSettings;
  logoUrl: string;
  faviconUrl: string;
}

const defaultSettings: AppSettings = {
  theme: {
    primaryColor: "#C9A84C",
    secondaryColor: "#2a2a2cff",
    fontFamily: "Inter",
    logoUrl: ""
  },
  business: {
    businessName: "Shalimar ERP",
    tagline: "Premium Event Management",
    address: "",
    phone: "",
    email: "",
    website: "",
    gstNumber: "",
    licenseNumber: "",
  },
  pwa: {
    appName: "",
    shortName: "",
    description: "",
    icon192: "",
    icon512: "",
    installPromptEnabled: true,
  },
  general: {
    language: "en",
    timezone: "Asia/Kolkata",
    dateFormat: "DD/MM/YYYY",
    currency: "INR",
    taxRate: "18",
    quotationValidityDays: "7",
    testingMode: false,
    quickLoginEmail: "",
    quickLoginPassword: "",
    paymentEditPassword: "",
  },
  logoUrl: "",
  faviconUrl: "",
};

interface SettingsContextType {
  settings: AppSettings;
  isLoading: boolean;
  refreshSettings: () => Promise<void>;
  updateSettings: (newSettings: Partial<AppSettings>) => void;
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

export function SettingsProvider({ children }: { children: React.ReactNode }) {
  const [settings, setSettings] = useState<AppSettings>(defaultSettings);
  const [isLoading, setIsLoading] = useState(true);
  const { isBackendAvailable } = useConnection();

  const loadSettingsFromStorage = useCallback(() => {
    if (typeof window === "undefined") return defaultSettings;
    
    const stored = localStorage.getItem("app_settings");
    if (stored) {
      try {
        return { ...defaultSettings, ...JSON.parse(stored) };
      } catch {
        return defaultSettings;
      }
    }
    return defaultSettings;
  }, []);

  const saveSettingsToStorage = useCallback((newSettings: AppSettings) => {
    if (typeof window === "undefined") return;
    localStorage.setItem("app_settings", JSON.stringify(newSettings));
  }, []);

  const fetchSettingsFromBackend = useCallback(async () => {
    // Skip backend fetch if backend is not available
    if (!isBackendAvailable) {
      console.warn('Backend not available, using default settings');
      return defaultSettings;
    }

    try {
      // Check if user is authenticated
      const hasToken = typeof window !== "undefined" && localStorage.getItem('token');
      
      let backendData;
      if (hasToken) {
        // Authenticated user - fetch all settings
        const response = await settingsApi.getAll();
        backendData = response.data || response;
      } else {
        // Public user - fetch only public settings (logo, business info)
        const response = await settingsApi.getPublic();
        backendData = response.data || response;
      }
      
      const merged: AppSettings = { ...defaultSettings };
      
      // Handle structured API response (new format)
      if (backendData.pwa) {
        // Map PWA settings
        merged.pwa = {
          appName: backendData.pwa.app_name || backendData.pwa.appName || defaultSettings.pwa.appName,
          shortName: backendData.pwa.short_name || backendData.pwa.shortName || defaultSettings.pwa.shortName,
          description: backendData.pwa.description || defaultSettings.pwa.description,
          icon192: backendData.pwa.icon_192 || backendData.pwa.icon192 || defaultSettings.pwa.icon192,
          icon512: backendData.pwa.icon_512 || backendData.pwa.icon512 || defaultSettings.pwa.icon512,
          installPromptEnabled: backendData.pwa.install_prompt_enabled ?? backendData.pwa.installPromptEnabled ?? defaultSettings.pwa.installPromptEnabled,
        };
        // Extract logo and favicon from PWA settings
        merged.logoUrl = backendData.pwa.main_logo || '';
        merged.faviconUrl = backendData.pwa.favicon || '';
      }
      
      if (backendData.business) {
        // Map business settings
        merged.business = {
          businessName: backendData.business.business_name || backendData.business.businessName || defaultSettings.business.businessName,
          tagline: backendData.business.tagline || defaultSettings.business.tagline,
          address: backendData.business.address || defaultSettings.business.address,
          phone: backendData.business.phone || defaultSettings.business.phone,
          email: backendData.business.email || defaultSettings.business.email,
          website: backendData.business.website || defaultSettings.business.website,
          gstNumber: backendData.business.gst_number || backendData.business.gstNumber || defaultSettings.business.gstNumber,
          licenseNumber: backendData.business.license_number || backendData.business.licenseNumber || defaultSettings.business.licenseNumber,
        };
      }
      
      if (backendData.general) {
        // Map general settings
        merged.general = {
          language: backendData.general.language || defaultSettings.general.language,
          timezone: backendData.general.timezone || defaultSettings.general.timezone,
          dateFormat: backendData.general.date_format || backendData.general.dateFormat || defaultSettings.general.dateFormat,
          currency: backendData.general.currency || defaultSettings.general.currency,
          taxRate: backendData.general.tax_rate || backendData.general.taxRate || defaultSettings.general.taxRate,
          quotationValidityDays: backendData.general.quotation_validity_days || backendData.general.quotationValidityDays || defaultSettings.general.quotationValidityDays,
          testingMode: backendData.general.testing_mode ?? backendData.general.testingMode ?? defaultSettings.general.testingMode,
          quickLoginEmail: backendData.general.quick_login_email || backendData.general.quickLoginEmail || defaultSettings.general.quickLoginEmail,
          quickLoginPassword: backendData.general.quick_login_password || backendData.general.quickLoginPassword || defaultSettings.general.quickLoginPassword,
          // Payment-edit password is write-only; never read back from the API.
          paymentEditPassword: "",
        };
      }
      
      if (backendData.email) {
        // Email settings are handled separately in SMTP section
        console.log('Email settings loaded:', backendData.email);
      }
      
      // Legacy support: Handle flat keys (old format)
      Object.entries(backendData).forEach(([key, value]: [string, any]) => {
        // Skip already processed nested objects
        if (['pwa', 'business', 'general', 'email'].includes(key)) return;
        
        if (key.startsWith('theme.')) {
          const subKey = key.replace('theme.', '') as keyof ThemeSettings;
          merged.theme[subKey] = value;
        } else if (key.startsWith('business.')) {
          const subKey = key.replace('business.', '') as keyof BusinessInfo;
          merged.business[subKey] = value;
        } else if (key.startsWith('pwa.')) {
          const subKey = key.replace('pwa.', '') as keyof PWASettings;
          if (subKey === 'installPromptEnabled') {
            merged.pwa[subKey] = value === 'true' || value === true;
          } else {
            merged.pwa[subKey] = value;
          }
        } else if (key.startsWith('general.')) {
          const subKey = key.replace('general.', '') as keyof GeneralSettings;
          (merged.general as any)[subKey] = value;
        } else if (key === 'theme.logoUrl') {
          merged.logoUrl = value;
        } else if (key === 'theme.faviconUrl') {
          merged.faviconUrl = value;
        }
      });
      
      return merged;
    } catch (error) {
      console.error("Failed to fetch settings from backend:", error);
      // Don't return null on network errors, return default settings to allow app to function
      if (error instanceof Error && (
        error.message.includes('Network error') || 
        error.message.includes('timed out') ||
        error.message.includes('Unable to connect')
      )) {
        console.warn('Using default settings due to network issues');
        return defaultSettings;
      }
      return null;
    }
  }, [isBackendAvailable]);

  const refreshSettings = useCallback(async () => {
    setIsLoading(true);
    try {
      // Try backend first
      const backendSettings = await fetchSettingsFromBackend();
      if (backendSettings) {
        setSettings(backendSettings);
        saveSettingsToStorage(backendSettings);
      } else {
        // Fallback to localStorage
        const storedSettings = loadSettingsFromStorage();
        setSettings(storedSettings);
      }
    } finally {
      setIsLoading(false);
    }
  }, [fetchSettingsFromBackend, loadSettingsFromStorage, saveSettingsToStorage]);

  const updateSettings = useCallback((newSettings: Partial<AppSettings>) => {
    setSettings((prev) => {
      const updated = {
        ...prev,
        ...newSettings,
        theme: { ...prev.theme, ...newSettings.theme },
        business: { ...prev.business, ...newSettings.business },
        pwa: { ...prev.pwa, ...newSettings.pwa },
        general: { ...prev.general, ...newSettings.general },
      };
      saveSettingsToStorage(updated);
      return updated;
    });
  }, [saveSettingsToStorage]);

  // Load settings on mount
  useEffect(() => {
    const initSettings = async () => {
      try {
        // First load from localStorage for immediate display
        const storedSettings = loadSettingsFromStorage();
        setSettings(storedSettings);
        
        // Then fetch from backend to update
        const backendSettings = await fetchSettingsFromBackend();
        if (backendSettings) {
          setSettings(backendSettings);
          saveSettingsToStorage(backendSettings);
        }
      } catch (error) {
        console.error("Error initializing settings:", error);
      } finally {
        setIsLoading(false);
      }
    };
    
    initSettings();
  }, [fetchSettingsFromBackend, loadSettingsFromStorage, saveSettingsToStorage]);

  return (
    <SettingsContext.Provider
      value={{
        settings,
        isLoading,
        refreshSettings,
        updateSettings,
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    // Return default settings instead of throwing error
    // This prevents crashes when component renders outside provider
    return {
      settings: defaultSettings,
      isLoading: false,
      refreshSettings: async () => {},
      updateSettings: () => {},
    };
  }
  return context;
}
